import {
  Component, ElementRef, EventEmitter, HostListener, Input, Output, computed, inject, signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageService } from '../../core/services/language.service';

interface CalendarDay {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  disabled: boolean;
}

/**
 * Sélecteur date + heure custom (popover calendrier + steppers heure/minute) -- remplace
 * <input type="datetime-local"> dont le calendrier natif est rendu par l'OS et ne peut pas
 * être stylé (même rationale que app-language-select vis-à-vis de <select>). Valeur au format
 * local 'YYYY-MM-DDTHH:mm' (identique au format natif) pour rester un remplacement direct.
 */
@Component({
  selector: 'app-date-time-picker',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './date-time-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./date-time-picker.component.scss'],
})
export class DateTimePickerComponent {
  @Input() ariaLabel?: string;
  @Input() placeholder?: string;

  @Input()
  set value(v: string | null | undefined) {
    this.applyValue(v || '');
  }
  get value(): string { return this._value; }

  /** Jour minimum sélectionnable (format 'YYYY-MM-DDTHH:mm' ou date seule) -- ex. la date de fin ne peut pas précéder la date de début. Grise les jours antérieurs dans le calendrier, en défense en profondeur de la validation portée par le formulaire parent. */
  @Input()
  set minValue(v: string | null | undefined) {
    this.minDate.set(this.parseDateOnly(v || ''));
  }

  @Output() valueChange = new EventEmitter<string>();

  private readonly elRef = inject(ElementRef<HTMLElement>);
  private readonly lang = inject(LanguageService).currentLang;

  private _value = '';
  open = signal(false);
  openUpward = signal(false);
  viewMonth = signal<Date>(this.startOfMonth(new Date()));
  selectedDate = signal<Date | null>(null);
  minDate = signal<Date | null>(null);
  hour = signal(0);
  minute = signal(0);

  readonly weekdayLabels = computed(() => {
    const fmt = new Intl.DateTimeFormat(this.lang(), { weekday: 'short' });
    // 1er janvier 2024 = lundi -> base fiable pour un en-tête lundi -> dimanche.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  });

  readonly monthLabel = computed(() => {
    const fmt = new Intl.DateTimeFormat(this.lang(), { month: 'long', year: 'numeric' });
    const label = fmt.format(this.viewMonth());
    return label.charAt(0).toUpperCase() + label.slice(1);
  });

  readonly calendarDays = computed<CalendarDay[]>(() => {
    const ref = this.viewMonth();
    const year = ref.getFullYear();
    const month = ref.getMonth();
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // 0 = lundi
    const gridStart = new Date(year, month, 1 - firstWeekday);
    const today = new Date();
    const sel = this.selectedDate();
    const min = this.minDate();
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      return {
        date: d,
        inMonth: d.getMonth() === month,
        isToday: this.sameDay(d, today),
        isSelected: sel !== null && this.sameDay(d, sel),
        disabled: min !== null && d.getTime() < min.getTime(),
      };
    });
  });

  readonly displayLabel = computed(() => {
    const d = this.selectedDate();
    if (!d) return '';
    const fmt = new Intl.DateTimeFormat(this.lang(), { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt.format(d)} · ${this.pad(this.hour())}:${this.pad(this.minute())}`;
  });

  readonly hourStr = computed(() => this.pad(this.hour()));
  readonly minuteStr = computed(() => this.pad(this.minute()));

  toggle(): void {
    const next = !this.open();
    if (next) {
      this.viewMonth.set(this.startOfMonth(this.selectedDate() ?? this.minDate() ?? new Date()));
      this.updateDropDirection();
    }
    this.open.set(next);
  }

  close(): void { this.open.set(false); }

  prevMonth(): void {
    const d = this.viewMonth();
    this.viewMonth.set(new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }

  nextMonth(): void {
    const d = this.viewMonth();
    this.viewMonth.set(new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }

  selectDay(day: CalendarDay): void {
    if (day.disabled) return;
    this.selectedDate.set(this.startOfDay(day.date));
    if (!day.inMonth) this.viewMonth.set(this.startOfMonth(day.date));
    this.emit();
  }

  adjustHour(delta: number): void {
    this.ensureDay();
    this.hour.set(((this.hour() + delta) % 24 + 24) % 24);
    this.emit();
  }

  adjustMinute(delta: number): void {
    this.ensureDay();
    this.minute.set(((this.minute() + delta) % 60 + 60) % 60);
    this.emit();
  }

  onHourInput(raw: string): void {
    const n = parseInt(raw, 10);
    if (isNaN(n)) return;
    this.ensureDay();
    this.hour.set(Math.min(23, Math.max(0, n)));
    this.emit();
  }

  onMinuteInput(raw: string): void {
    const n = parseInt(raw, 10);
    if (isNaN(n)) return;
    this.ensureDay();
    this.minute.set(Math.min(59, Math.max(0, n)));
    this.emit();
  }

  clear(): void {
    this.selectedDate.set(null);
    this.hour.set(0);
    this.minute.set(0);
    this._value = '';
    this.valueChange.emit('');
    this.close();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.elRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void { this.close(); }

  /** Bascule le popover au-dessus du champ quand la place manque en dessous (bas de page/carte). */
  private updateDropDirection(): void {
    const rect = this.elRef.nativeElement.getBoundingClientRect();
    const estimatedPopoverHeight = 360;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    this.openUpward.set(spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow);
  }

  private ensureDay(): void {
    if (!this.selectedDate()) this.selectedDate.set(this.startOfDay(new Date()));
  }

  private emit(): void {
    const d = this.selectedDate();
    if (!d) { this._value = ''; this.valueChange.emit(''); return; }
    this._value = `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(this.hour())}:${this.pad(this.minute())}`;
    this.valueChange.emit(this._value);
  }

  private applyValue(v: string): void {
    this._value = v;
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v);
    if (!match) {
      this.selectedDate.set(null);
      this.hour.set(0);
      this.minute.set(0);
      return;
    }
    const [, y, mo, da, h, mi] = match;
    const d = new Date(Number(y), Number(mo) - 1, Number(da));
    this.selectedDate.set(d);
    this.hour.set(Number(h));
    this.minute.set(Number(mi));
    this.viewMonth.set(this.startOfMonth(d));
  }

  private parseDateOnly(v: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    if (!match) return null;
    const [, y, mo, da] = match;
    return new Date(Number(y), Number(mo) - 1, Number(da));
  }

  private startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
  private startOfDay(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

  private sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private pad(n: number): string { return String(n).padStart(2, '0'); }
}
